import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Avatar } from '../components/Avatar';
import { api, errorMessage } from '../lib/api';
import type { PublicUser } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { Field } from '../components/Field';
import { Banner } from '../components/Banner';
import { Sheet } from '../components/Sheet';
import { useConfirm } from '../components/Confirm';
import { useWorkspace } from '../store/workspace';
import { formatPhone, isPhone, normalisePhone } from '../lib/phone';
import { formatDate, timeAgo } from '../lib/when';

interface ResetOutcome {
  member: PublicUser;
  password: string;
  sessionsEnded: number;
}

/**
 * The teachers of one centre, as accounts rather than as rows.
 *
 * Everything used to happen in the table: editing unfolded inside a row, a new
 * password appeared as a panel above it, adding a teacher pushed the whole
 * list down, and three teachers meant nine identical buttons in one strip. It
 * was never clear which account you were acting on.
 *
 * So each teacher is a card, and opening one opens a window over the page with
 * that account and nothing else in it — their details, their password, their
 * access. The roster stays visible behind, which is the point: you are looking
 * at one file out of a drawer, not at a different screen.
 *
 * Platform administrators only. A teacher managing colleagues would mean one
 * compromised account could take over every account in the centre, which is
 * the same reason the server refuses it.
 */
export function TeachersPage() {
  const organizationName = useWorkspace((w) => w.organizationName);
  const organizationId = useWorkspace((w) => w.organizationId);

  const [teachers, setTeachers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const { ask, dialog } = useConfirm();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get<{ members: PublicUser[] }>('/users', {
        params: { role: 'INSTRUCTOR' },
      });
      setTeachers(data.members);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load teachers'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (organizationId) void load();
  }, [organizationId]);

  const open = useMemo(() => teachers.find((t) => t.id === openId) ?? null, [teachers, openId]);

  if (!organizationId) {
    return (
      <Banner tone="error">
        Open a centre from Organisations first — teachers belong to a centre, not to the platform.
      </Banner>
    );
  }

  return (
    <>
      {dialog}
      <PageHeader
        title="Teachers"
        meta={
          <p className="muted">
            <span className="mono">{teachers.length}</span>{' '}
            {teachers.length === 1 ? 'account' : 'accounts'}
            {organizationName ? ` at ${organizationName}` : ''}
          </p>
        }
        actions={
          <button className="btn btn--primary" onClick={() => setAdding(true)}>
            Add a teacher
          </button>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        {notice && <Banner tone="success">{notice}</Banner>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : teachers.length === 0 ? (
          <div className="empty">
            <p>No teachers in this centre.</p>
            <p className="muted">Add one so somebody can import papers and enrol students.</p>
          </div>
        ) : (
          <div className="roster">
            {teachers.map((teacher, index) => (
              <button
                key={teacher.id}
                type="button"
                className="roster__card"
                // A short stagger on load: the roster arrives as a list being
                // dealt out, which reads as order rather than as decoration.
                style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
                onClick={() => setOpenId(teacher.id)}
              >
                <div className="roster__top">
                  <Avatar name={teacher.fullName} />
                  <span>
                    <span className="roster__name">{teacher.fullName}</span>
                    <br />
                    <span className="roster__phone">{formatPhone(teacher.phone)}</span>
                  </span>
                </div>
                <div className="roster__foot">
                  <Status teacher={teacher} />
                  <span className="roster__open">Open →</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <TeacherWindow
        teacher={open}
        onClose={() => setOpenId(null)}
        onChanged={async (message) => {
          setNotice(message);
          setError(null);
          await load();
        }}
        onRemoved={async (message) => {
          setOpenId(null);
          setNotice(message);
          setError(null);
          await load();
        }}
        ask={ask}
      />

      <AddTeacherWindow
        open={adding}
        onClose={() => setAdding(false)}
        onAdded={async (message) => {
          setAdding(false);
          setNotice(message);
          setError(null);
          await load();
        }}
      />
    </>
  );
}

/** What is true of this account, in one line: the reason to open it, or not. */
function Status({ teacher }: { teacher: PublicUser }) {
  if (teacher.mustChangePassword) {
    return <span className="pip pip--waiting">Temporary password</span>;
  }
  if (!teacher.lastLoginAt) return <span className="pip pip--cold">Never signed in</span>;
  return <span className="pip pip--live">Signed in {timeAgo(teacher.lastLoginAt)}</span>;
}

/**
 * One teacher's account, open.
 *
 * The three things an administrator ever does to a colleague's account, in the
 * order they are needed and each said in full: correct the details, hand over
 * a new password, take the account away.
 */
function TeacherWindow({
  teacher,
  onClose,
  onChanged,
  onRemoved,
  ask,
}: {
  teacher: PublicUser | null;
  onClose: () => void;
  onChanged: (message: string) => Promise<void>;
  onRemoved: (message: string) => Promise<void>;
  ask: ReturnType<typeof useConfirm>['ask'];
}) {
  const [edit, setEdit] = useState({ fullName: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reset, setReset] = useState<ResetOutcome | null>(null);
  const [copied, setCopied] = useState(false);

  // Each account opens fresh: a password shown for one teacher must never
  // still be on screen when the next one is opened.
  useEffect(() => {
    setEdit({ fullName: teacher?.fullName ?? '', phone: formatPhone(teacher?.phone ?? '') });
    setReset(null);
    setError(null);
    setCopied(false);
  }, [teacher?.id]);

  if (!teacher) return null;

  const phoneValid = isPhone(edit.phone);
  const dirty =
    edit.fullName.trim() !== teacher.fullName ||
    (phoneValid && normalisePhone(edit.phone) !== teacher.phone);

  async function close() {
    if (dirty) {
      const leave = await ask({
        title: 'Close without saving?',
        body: <p>The changes to their name or number will be lost.</p>,
        confirmLabel: 'Discard changes',
        tone: 'danger',
      });
      if (!leave) return;
    }
    onClose();
  }

  async function save() {
    if (!teacher) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {};
      if (edit.fullName.trim() !== teacher.fullName) body.fullName = edit.fullName.trim();
      if (normalisePhone(edit.phone) !== teacher.phone) body.phone = edit.phone;
      await api.patch(`/users/${teacher.id}`, body);
      await onChanged(`${edit.fullName.trim()} updated.`);
    } catch (err) {
      setError(errorMessage(err, 'Could not save those changes'));
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (!teacher) return;
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const { data } = await api.post<ResetOutcome>(`/users/${teacher.id}/reset-password`);
      setReset(data);
    } catch (err) {
      setError(errorMessage(err, 'Could not reset that password'));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!teacher) return;
    const yes = await ask({
      title: `Remove ${teacher.fullName}?`,
      body: (
        <>
          <p>They will not be able to sign in. Their papers stay in the library.</p>
          <p>
            Their students will have no teacher until you give them to one on the Students page,
            and no other teacher sees them in the meantime.
          </p>
        </>
      ),
      confirmLabel: 'Remove teacher',
      tone: 'danger',
    });
    if (!yes) return;

    setBusy(true);
    setError(null);
    try {
      await api.delete(`/users/${teacher.id}`);
      await onRemoved(`${teacher.fullName} can no longer sign in.`);
    } catch (err) {
      setError(errorMessage(err, 'Could not remove that teacher'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open
      eyebrow="Teacher account"
      title={
        <span className="sheet__who">
          <Avatar name={teacher.fullName} size="lg" />
          {teacher.fullName}
        </span>
      }
      aside={
        <>
          <span className="mono">{formatPhone(teacher.phone)}</span>
          <span>·</span>
          <span>Added {formatDate(teacher.createdAt)}</span>
          <span>·</span>
          <Status teacher={teacher} />
        </>
      }
      label={`${teacher.fullName} — teacher account`}
      onClose={() => void close()}
      footer={
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="field__hint">
            {dirty ? 'Unsaved changes to their details.' : 'Everything here is saved.'}
          </span>
          <button className="btn" onClick={() => void close()}>
            Close
          </button>
        </div>
      }
    >
      {error && <Banner tone="error">{error}</Banner>}

      <section className="sheet__section">
        <p className="eyebrow">Details</p>
        <div className="form-grid">
          <Field
            label="Name"
            value={edit.fullName}
            onChange={(e) => setEdit((f) => ({ ...f, fullName: e.target.value }))}
          />
          <Field
            label="Sign-in number"
            type="tel"
            inputMode="numeric"
            value={edit.phone}
            onChange={(e) => setEdit((f) => ({ ...f, phone: e.target.value }))}
            error={edit.phone && !phoneValid ? 'Use 6 to 15 digits.' : null}
            hint={
              phoneValid && normalisePhone(edit.phone) !== teacher.phone
                ? `They will sign in with ${formatPhone(normalisePhone(edit.phone))}`
                : 'What they sign in with. It need not be a real phone.'
            }
          />
        </div>
        <div className="row">
          <button
            className="btn btn--primary"
            disabled={busy || !dirty || !phoneValid || edit.fullName.trim().length < 2}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save details'}
          </button>
          {dirty && (
            <button
              className="btn btn--ghost"
              onClick={() =>
                setEdit({ fullName: teacher.fullName, phone: formatPhone(teacher.phone) })
              }
            >
              Undo
            </button>
          )}
        </div>
      </section>

      <section className="sheet__section">
        <p className="eyebrow">Password</p>
        {reset ? (
          <>
            <p className="muted" style={{ maxWidth: '48ch' }}>
              Hand this over now. It is stored only as a hash, so nobody — including you — can look
              it up later. If it is lost, reset it again.
            </p>
            <div className="row" style={{ gap: 'var(--s-4)' }}>
              <code className="newpassword mono">{reset.password}</code>
              <button
                className="btn"
                onClick={() =>
                  void navigator.clipboard?.writeText(reset.password).then(() => setCopied(true))
                }
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            {reset.sessionsEnded > 0 && (
              <p className="field__hint">
                Signed out of <span className="mono">{reset.sessionsEnded}</span>{' '}
                {reset.sessionsEnded === 1 ? 'device' : 'devices'}, so the old password no longer
                opens anything.
              </p>
            )}
          </>
        ) : (
          <>
            <p className="muted" style={{ maxWidth: '48ch' }}>
              There is no email on this platform, so a forgotten password is replaced here and read
              out in person. The new one is shown once.
            </p>
            <div className="row">
              <button className="btn" disabled={busy} onClick={() => void resetPassword()}>
                {busy ? 'Working…' : 'Reset password'}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="sheet__section">
        <p className="eyebrow">Access</p>
        <p className="muted" style={{ maxWidth: '48ch' }}>
          Removing the account closes it for good. The papers they imported, the students they
          enrolled and every result stay exactly where they are.
        </p>
        <div className="row">
          <button className="btn btn--danger" disabled={busy} onClick={() => void remove()}>
            Remove from this centre
          </button>
        </div>
      </section>
    </Sheet>
  );
}

/** A new account, in its own window rather than unfolding over the roster. */
function AddTeacherWindow({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: (message: string) => Promise<void>;
}) {
  const [form, setForm] = useState({ fullName: '', phone: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ fullName: '', phone: '', password: '' });
      setError(null);
    }
  }, [open]);

  async function add(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/users', { ...form, role: 'INSTRUCTOR' });
      await onAdded(
        `${form.fullName} can now sign in with ${formatPhone(normalisePhone(form.phone))}.`,
      );
    } catch (err) {
      setError(errorMessage(err, 'Could not add that teacher'));
    } finally {
      setBusy(false);
    }
  }

  const ready = form.fullName.trim().length >= 2 && isPhone(form.phone) && form.password.length >= 8;

  return (
    <Sheet
      open={open}
      eyebrow="New account"
      title="Add a teacher"
      onClose={onClose}
      footer={
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn--primary" disabled={busy || !ready} onClick={(e) => void add(e)}>
            {busy ? 'Adding…' : 'Add teacher'}
          </button>
        </div>
      }
    >
      {error && <Banner tone="error">{error}</Banner>}

      <form className="sheet__section" onSubmit={(e) => void add(e)}>
        <p className="muted" style={{ maxWidth: '48ch' }}>
          They get the same screens as the teacher who owns this centre — its papers, its students
          and its results.
        </p>
        <Field
          label="Name"
          required
          autoFocus
          value={form.fullName}
          onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
        />
        <Field
          label="Sign-in number"
          type="tel"
          inputMode="numeric"
          required
          placeholder="90 123 45 67"
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          error={form.phone && !isPhone(form.phone) ? 'Use 6 to 15 digits.' : null}
          hint={
            form.phone && isPhone(form.phone)
              ? `They will sign in with ${formatPhone(normalisePhone(form.phone))}`
              : 'What the teacher will sign in with.'
          }
        />
        <Field
          label="Temporary password"
          type="text"
          required
          minLength={8}
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          hint="At least 8 characters. Hand it over for them to change."
        />
        {/* The submit the footer button stands in for, so Return works. */}
        <button type="submit" hidden />
      </form>
    </Sheet>
  );
}
